import React, { useState, useEffect, useRef, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Toolbar } from 'primereact/toolbar';
import { ProgressSpinner } from 'primereact/progressspinner';
import { FilterMatchMode } from 'primereact/api';

import { EvaluationService } from '../../service/EvaluationService';
import { translatedMessage } from '../../service/LanguageService';
import { useNavigate } from 'react-router-dom';

import CommitteeEditDialog from './CommitteeEditDialogComponent';
import DataTableUtils from '../../utilities/DataTableUtils';
import DeleteModalComponent from '../DeleteModalComponent';
import EvaluationDataTableUtils from '../../utilities/EvaluationDataTableUtils';
import EnumService from '../../service/EnumService';
import CommitteeUsedRoles from './CommitteeUsedRoles';
import ExportButtonComponent from '../ExportButtonComponent';
import GeneralUtils from '../../utilities/GeneralUtils';
import SecurityService from '../../service/SecurityService';
import { Message } from 'primereact/message';
import CompetitionUtils from '../../utilities/CompetitionUtils';

const tableHeader = [
    translatedMessage("generic.nameRo"),
    translatedMessage("competition.competition"),
    translatedMessage("generic.type"),
    translatedMessage("generic.startDate"),
    translatedMessage("generic.endDate"),
    translatedMessage("generic.status"),
    translatedMessage("committee.member.roles")
]

const CommitteeListComponent = (props) => {
    const [committees, setCommittees] = useState([]);
    const [selectedCommittee, setSelectedCommittee] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [canEdit, setCanEdit] = useState(false);
    const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
    const [editDialogVisible, setEditDialogVisible] = useState(false);
    const [competition, setCompetition] = useState(null);
    const [committeeTypes, setCommitteeTypes] = useState([]);
    const [existingCommitteeTypes, setExistingCommitteeTypes] = useState([]);
    const [hasCreatePermission, setHasCreatePermission] = useState(false);
    const [hasEditPermission, setHasEditPermission] = useState(false);
    const [hasViewPermission, setHasViewPermission] = useState(false);

    const [filters] = useState({
        'name': { value: null, matchMode: FilterMatchMode.CONTAINS },
        'type': { value: null, matchMode: FilterMatchMode.IN },
        'active': { value: null, matchMode: FilterMatchMode.IN },
    });

    const toast = useRef(null);
    const dt = useRef(null);
    const navigate = useNavigate();

    const evaluationService = useMemo(() => new EvaluationService(), [])

    useEffect(() => {
        setIsLoading(true);

        const getCommitteeTypes = async () => {
            await EnumService.getEnumByNameAndParam('EvaluationCommitteeType', props?.competition?.id).then((types) => setCommitteeTypes(types));
        };

        const checkCreatePermission = async () => {
            setHasCreatePermission(await SecurityService.check('CAN_CREATE_COMPETITION_COMMITTEE', props?.competition?.id));
        };

        const checkEditPermission = async () => {
            setHasEditPermission(await SecurityService.check('CAN_EDIT_COMPETITION_COMMITTEE', props?.competition?.id));
        };

        const checkViewPermission = async () => {
            setHasViewPermission(await SecurityService.check('CAN_VIEW_COMPETITION_COMMITTEE', props?.competition?.id));
        };

        const getCommittees = async () => {
            await getCommitteeList(props?.competition?.id)
            setCompetition(props?.competition)
            setCanEdit(props?.canEdit ? true : false)
            checkCreatePermission();
            checkEditPermission();
            checkViewPermission();
        };

        getCommitteeTypes()
        getCommittees()

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props, evaluationService]);

    const getCommitteeList = async () => {
        await evaluationService.getCommitteeListByCompetitionId(props?.competition?.id)
            .then(async (_committees) => {
                setCommittees(_committees);

                let _existingTypes = []
                _committees.forEach((committee) => {
                    _existingTypes.push(committee.type)
                })
                setExistingCommitteeTypes(_existingTypes)

                setIsLoading(false);
            })
            .catch((error) => toast.current.show({ severity: 'error', summary: translatedMessage(error), life: 5000 }));
    }


    const updateList = async (savedCommittee, isNew, success, error = null) => {
        if (success) {
            toast?.current?.show({ severity: 'success', summary: translatedMessage("generic.save.success") })
            getCommitteeList();
        } else {
            toast?.current?.show({ severity: 'error', summary: translatedMessage(error), life: 5000 })
        }
    };

    const showNewDialog = () => {
        setEditDialogVisible(true);
    };

    const showEditDialog = (committee) => {
        setSelectedCommittee(committee);
        setEditDialogVisible(true);
    };

    const hideEditDialog = () => {
        setSelectedCommittee(null);
        setEditDialogVisible(false);
    };

    const showCommitteePage = (committee) => {
        navigate(`${props?.viewCommitteeLink}/${committee.id}`);
    };

    const showDeleteDialog = (committee) => {
        setSelectedCommittee(committee);
        setDeleteDialogVisible(true);
    };

    const hideDeleteDialog = () => {
        setDeleteDialogVisible(false);
    };

    const deleteCommittee = () => {
        evaluationService.deleteCommittee(selectedCommittee.id)
            .then(() => {
                getCommitteeList();
                toast.current.show({ severity: 'success', summary: translatedMessage('generic.delete.success') });
            })
            .catch((error) => {
                toast.current.show({ severity: 'error', summary: translatedMessage(error), life: 5000 });
            })
            .finally(() => {
                setDeleteDialogVisible(false);
                setSelectedCommittee(null);
            });
    };

    const leftToolbarTemplate = () => {
        return (
            <React.Fragment>
                {canEdit &&
                    <div className="my-2">
                        {hasCreatePermission &&
                            <Button label={translatedMessage('committee.newCommittee')} icon="pi pi-plus" className="p-button-primary mr-2" onClick={showNewDialog} />
                        }
                    </div>
                }
            </React.Fragment>
        );
    };

    const handleExportRoles = (_roles) => {
        let response = ""

        _roles?.forEach((role) => {
            response += response ? "; " : ""
            response += translatedMessage('committee.member.' + role.name) + " (" + role.number + ")"
        })

        return response
    }

    const handleExport = () => {
        let exportData = committees.map(item => ({ ...item }));
        exportData.map(item => {
            let exportItem = item
            exportItem.active = translatedMessage('committee.' + (item.active ? 'ACTIVE' : 'INACTIVE'))
            exportItem.type = translatedMessage((CompetitionUtils.isJobCompetition(item.competitionType) ? "job." : "") + "EvaluationCommitteeType." + item.type)
            exportItem.addedRoles = handleExportRoles(item.addedRoles)
            delete exportItem.competitionId
            delete exportItem.competitionType
            delete exportItem.folderId
            delete exportItem.description

            return item;
        })

        return exportData
    }

    const rightToolbarTemplate = () => {
        return (
            <ExportButtonComponent
                getExportData={handleExport}
                header={tableHeader}
                fileName={GeneralUtils.replaceSpaces(translatedMessage("competition.committees"))}
            />
        );
    };

    const dateBodyTemplate = (rowData) => {
        return DataTableUtils.dateIntervalTemplate(rowData.startDate, rowData.endDate)
    }

    const rolesBodyTemplate = (rowData) => {
        return <CommitteeUsedRoles roles={rowData.addedRoles} />;
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="actions flex flex-wrap align-items-center justify-content-end">
                {hasViewPermission &&
                    <Button icon="pi pi-eye" className="p-button-rounded p-button-primary m-1" onClick={() => showCommitteePage(rowData)}
                        tooltip={translatedMessage("generic.view")} tooltipOptions={{ position: 'top' }} />
                }
                {canEdit && hasEditPermission &&
                    <Button icon="pi pi-pencil" className="p-button-rounded p-button-info m-1" onClick={() => showEditDialog(rowData)}
                        tooltip={translatedMessage("generic.edit")} tooltipOptions={{ position: 'top' }}
                        disabled={CompetitionUtils.isJobCompetition(competition.type) && rowData?.type === "COMPETITION"} />
                }
                {canEdit && hasCreatePermission &&
                    <Button icon="pi pi-trash" className="p-button-rounded p-button-danger m-1" onClick={() => showDeleteDialog(rowData)}
                        tooltip={translatedMessage("generic.delete")} tooltipOptions={{ position: 'top' }}
                        disabled={CompetitionUtils.isJobCompetition(competition.type) && rowData?.type === "COMPETITION"} />
                }
            </div>
        );
    };

    const committeeTypeTemplate = (_committeeType) => {
        return <div>{committeeTypes?.find((type) => type.value === _committeeType)?.label}</div>;
    };

    if (isLoading) {
        return (
            <div className="w-full flex align-items-center">
                <Toast ref={toast} />
                <ProgressSpinner />
            </div>
        );
    } else {
        return (
            <>
                <Toast ref={toast} />
                <div>
                    <h5 className='mb-1 font-weight-normal'>{translatedMessage("competition.committees")}</h5>
                </div>
                {CompetitionUtils.isJobCompetition(competition?.type) && props?.warningMessage &&
                    <div className='mt-1 mb-1'><Message severity="warn" text={translatedMessage(props?.warningMessage)} /></div>
                }
                {CompetitionUtils.isJobCompetition(competition?.type) && props?.infoMessage &&
                    <div className='mt-1 mb-1'><Message severity="warn" text={translatedMessage(props?.infoMessage)} /></div>
                }

                <Toolbar left={leftToolbarTemplate} right={rightToolbarTemplate} className="px-0 pt-0"></Toolbar>

                <DataTable
                    ref={dt}
                    value={committees}
                    dataKey="id"
                    paginator
                    rows={DataTableUtils.defalRowsPerPage()}
                    rowsPerPageOptions={DataTableUtils.rowsPerPageOptions()}
                    className="datatable-responsive pcn-datatable"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate={"{first} - {last} " + translatedMessage('generic.of') + " {totalRecords}"}
                    emptyMessage={translatedMessage('generic.tableEmptyMessage')}
                    responsiveLayout="stack"
                    filterDisplay="row"
                    filters={filters}
                    globalFilterFields={['name', 'type', 'active']}
                >
                    <Column
                        field="name"
                        header={translatedMessage('generic.nameRo')}
                        sortable
                        headerStyle={{ width: '20%', minWidth: '10rem' }}
                        filter
                        showFilterMenu={false}
                    />
                    <Column
                        field="type"
                        header={translatedMessage('generic.type')}
                        sortable
                        body={(e) => committeeTypeTemplate(e.type)}
                        headerStyle={{ width: '30%', minWidth: '10rem' }}
                        filter
                        filterElement={(e) => EvaluationDataTableUtils.committeeTypeFilterTemplate(e, committeeTypes)}
                        showFilterMenu={false}
                    />
                    <Column
                        field="active"
                        header={translatedMessage('generic.status')}
                        body={(e) => EvaluationDataTableUtils.committeeStatusBodyTemplate(e.active)}
                        headerStyle={{ width: '5%', minWidth: '8rem' }}
                        filter
                        filterElement={(e) => EvaluationDataTableUtils.committeeStatusFilterTemplate(e)}
                        showFilterMenu={false}
                    />
                    <Column
                        field="startDate"
                        header={translatedMessage('generic.interval')}
                        sortable
                        body={dateBodyTemplate}
                        headerStyle={{ width: '5%', minWidth: '8rem' }}
                    />
                    <Column
                        field="addedRoles"
                        header={translatedMessage('committee.member.roles')}
                        body={rolesBodyTemplate}
                        headerStyle={{ width: '15%', minWidth: '8rem' }}
                    />
                    <Column body={actionBodyTemplate} bodyStyle={{ textAlign: 'right' }} />
                </DataTable>


                {editDialogVisible &&
                    <CommitteeEditDialog
                        value={selectedCommittee}
                        visible={editDialogVisible}
                        afterSave={updateList}
                        visibleSetter={hideEditDialog}
                        competition={competition}
                        existingCommitteeTypes={existingCommitteeTypes}
                    />
                }

                {deleteDialogVisible &&
                    <DeleteModalComponent
                        visible={deleteDialogVisible}
                        item={selectedCommittee ? selectedCommittee.name : ''}
                        closeDialog={hideDeleteDialog}
                        deleteRecord={() => deleteCommittee()}
                    />
                }

            </>
        );
    }
};

export default CommitteeListComponent;
